/**
 * Aktenrahmen — feste Abschnittsfolge links, ein Gegenstand rechts.
 * Klasse AKTE_HUB_CLICK_NOT_CHAT bleibt: Klick öffnet die sichtbare Akte,
 * ARKO ist optional. Keine neue Security-Klasse.
 *
 * Lokal nur Abschnitts-ID und ggf. Raum-ID pro Akte. Kein Notiztext, kein Titel.
 * Chats: nur Store-Katalog (GET element-rooms, source store) mit derselben akte_id.
 */

import {
  readElementRoomsResponse,
  type ElementRoomOption,
} from './elementSeatInvite';
import { isHonestMatrixRoomId, isStubMatrixRoomId } from './portalMatrixOrigins';

/** Immer sichtbar. Notiz und Element-Chats sind keine Aktenabschnitte. */
export const PORTAL_AKTE_RAHMEN_BASE_SECTIONS = [
  { id: 'verlauf', label: 'Verlauf' },
  { id: 'dokumente', label: 'Dokumente' },
  { id: 'fristen', label: 'Fristen' },
] as const;

/** Eigene Zeile, nur wenn ARKO gebucht ist. Nicht die Element-Räume. */
export const PORTAL_AKTE_RAHMEN_ARKO_SECTION = { id: 'arko', label: 'ARKO-Akte' } as const;

/** Immer da, auch ohne ARKO. Nur Dokumente dieser Akte. */
export const PORTAL_AKTE_RAHMEN_OO_SECTION = { id: 'onlyoffice', label: 'OnlyOffice' } as const;

export type PortalAkteRahmenSectionId =
  | (typeof PORTAL_AKTE_RAHMEN_BASE_SECTIONS)[number]['id']
  | (typeof PORTAL_AKTE_RAHMEN_ARKO_SECTION)['id']
  | (typeof PORTAL_AKTE_RAHMEN_OO_SECTION)['id'];

export function portalAkteRahmenSections(arkoBooked: boolean): Array<{
  id: PortalAkteRahmenSectionId;
  label: string;
}> {
  const base = PORTAL_AKTE_RAHMEN_BASE_SECTIONS.map((section) => ({
    id: section.id,
    label: section.label,
  }));
  const office = {
    id: PORTAL_AKTE_RAHMEN_OO_SECTION.id,
    label: PORTAL_AKTE_RAHMEN_OO_SECTION.label,
  };
  if (!arkoBooked) return [...base, office];
  return [
    ...base,
    { id: PORTAL_AKTE_RAHMEN_ARKO_SECTION.id, label: PORTAL_AKTE_RAHMEN_ARKO_SECTION.label },
    office,
  ];
}

/** Chat ohne Akte gehört nur in den Hauptreiter. Chat mit Akte nur in genau diese Akte. */
export function arkoThreadVisibleInScope(
  thread: { akteId?: string | null } | null | undefined,
  scope: { kind: 'haupt' } | { kind: 'akte'; akteId: string },
): boolean {
  const bound = String(thread?.akteId || '').trim();
  if (scope.kind === 'haupt') return bound === '';
  const akteId = scope.akteId.trim();
  if (!akteId) return false;
  return bound === akteId;
}

/** Aktion nur, wenn der Chat an genau dieser offenen Akte hängt. */
export function akteChatActionAllowed(
  thread: { akteId?: string | null } | null | undefined,
  openAkteId: string,
): boolean {
  return arkoThreadVisibleInScope(thread, { kind: 'akte', akteId: openAkteId });
}

export type AkteChatActionRow = {
  id: string;
  akteId?: string | null;
  title: string;
  archived?: boolean;
};

/** Umbenennen, Archivieren, Löschen. Fremde Akte und Chat ohne Akte bleiben unverändert. */
export function applyAkteChatAction<T extends AkteChatActionRow>(
  rows: readonly T[],
  action:
    | { type: 'rename'; id: string; title: string }
    | { type: 'archive'; id: string }
    | { type: 'delete'; id: string },
  openAkteId: string,
): T[] {
  const target = rows.find((row) => row.id === action.id);
  if (!target || !akteChatActionAllowed(target, openAkteId)) return [...rows];
  if (action.type === 'rename') {
    const title = action.title.trim();
    if (!title) return [...rows];
    return rows.map((row) => (row.id === target.id ? { ...row, title } : row));
  }
  if (action.type === 'archive') {
    return rows.map((row) => (row.id === target.id ? { ...row, archived: true } : row));
  }
  return rows.filter((row) => row.id !== target.id);
}

/** Klick ins Fenster lässt es offen. Klick daneben schließt es. */
export function aktePopoversAfterOutsideClick(inside: boolean): 'keep' | 'close' {
  return inside ? 'keep' : 'close';
}

/** Neuer Chat: Hauptreiter ohne Akten-ID, Akte nur mit dieser ID. */
export function newArkoChatAkteId(
  scope: { kind: 'haupt' } | { kind: 'akte'; akteId: string },
): string | undefined {
  if (scope.kind === 'haupt') return undefined;
  const akteId = scope.akteId.trim();
  return akteId || undefined;
}

/** Dieselbe Prüfung wie die Sidebar: Reiter ARKO ist die View `chat`. Kein neues Flag. */
export function portalAkteHasArko(navIds: readonly string[]): boolean {
  return navIds.includes('chat');
}

/** Nur Abschnitts-ID und Raum-ID. Kein Mandantentext. */
export const PORTAL_AKTE_RAHMEN_STORAGE_KEY = 'arkorbit.portal.akteRahmen.v1';

const SECTION_ID_SET: ReadonlySet<string> = new Set([
  ...PORTAL_AKTE_RAHMEN_BASE_SECTIONS.map((section) => section.id),
  PORTAL_AKTE_RAHMEN_ARKO_SECTION.id,
  PORTAL_AKTE_RAHMEN_OO_SECTION.id,
]);

export function isPortalAkteRahmenSectionId(raw: string): raw is PortalAkteRahmenSectionId {
  return SECTION_ID_SET.has(raw);
}

export function portalAkteRahmenSectionLabel(id: PortalAkteRahmenSectionId): string {
  const row = portalAkteRahmenSections(true).find((section) => section.id === id);
  return row ? row.label : 'Verlauf';
}

function safeAkteStorageKey(akteId: string): string {
  const id = akteId.trim();
  if (!/^[A-Za-z0-9_-]{1,40}$/.test(id)) return '';
  return id;
}

export type PortalAkteRahmenMemory = {
  sectionId: PortalAkteRahmenSectionId;
  roomId: string;
  /** Nur eine ID, kein Titel und kein Akteninhalt. */
  docId: string;
  chatId: string;
};

const VERLAUF_MEMORY: PortalAkteRahmenMemory = {
  sectionId: 'verlauf',
  roomId: '',
  docId: '',
  chatId: '',
};

function safeFocusId(raw?: string | null): string {
  const id = raw?.trim() ?? '';
  if (!/^[A-Za-z0-9_-]{1,80}$/.test(id)) return '';
  return id;
}

function readMemoryMap(): Record<string, unknown> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(PORTAL_AKTE_RAHMEN_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * Erstes Öffnen ohne Eintrag: Verlauf.
 * Gemerkte Notiz oder ARKO ohne Buchung fällt auf Verlauf zurück.
 */
export function recallPortalAkteRahmen(akteId: string, arkoBooked = false): PortalAkteRahmenMemory {
  const key = safeAkteStorageKey(akteId);
  if (!key) return VERLAUF_MEMORY;
  const row = readMemoryMap()[key];
  if (!row || typeof row !== 'object' || Array.isArray(row)) return VERLAUF_MEMORY;
  const rec = row as Record<string, unknown>;
  const section = typeof rec.s === 'string' ? rec.s : '';
  if (section === 'notiz' || section === 'chats') return VERLAUF_MEMORY;
  if (!isPortalAkteRahmenSectionId(section)) return VERLAUF_MEMORY;
  if (section === 'arko' && !arkoBooked) return VERLAUF_MEMORY;
  const docId = section === 'onlyoffice' ? safeFocusId(typeof rec.d === 'string' ? rec.d : '') : '';
  const chatId = section === 'arko' ? safeFocusId(typeof rec.c === 'string' ? rec.c : '') : '';
  return { sectionId: section, roomId: '', docId, chatId };
}

/**
 * Schreibt die Reiter-ID und, nur bei OnlyOffice oder ARKO, eine ID.
 * Kein Raum, kein Titel, kein Notiztext.
 */
export function rememberPortalAkteRahmen(
  akteId: string,
  sectionId: string,
  _roomId?: string,
  focus?: { docId?: string | null; chatId?: string | null },
): void {
  const key = safeAkteStorageKey(akteId);
  if (!key || !isPortalAkteRahmenSectionId(sectionId)) return;
  if (typeof localStorage === 'undefined') return;
  const map = readMemoryMap();
  const next: { s: PortalAkteRahmenSectionId; d?: string; c?: string } = { s: sectionId };
  if (sectionId === 'onlyoffice') {
    const docId = safeFocusId(focus?.docId);
    if (docId) next.d = docId;
  }
  if (sectionId === 'arko') {
    const chatId = safeFocusId(focus?.chatId);
    if (chatId) next.c = chatId;
  }
  map[key] = next;
  try {
    localStorage.setItem(PORTAL_AKTE_RAHMEN_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* Speicher voll oder gesperrt — die Akte bleibt benutzbar. */
  }
}

/** OnlyOffice|ARKO in der Akte: nur Prozent und zu/offen. Kein Titel, kein Chattext. */
const PORTAL_AKTE_OO_SPLIT_KEY = 'portal-akte-oo-split-v1';
const AKTE_OO_EDITOR_DEFAULT = 62;

export type AkteOoSplitMemory = {
  editorPct: number;
  chatClosed: boolean;
};

const AKTE_OO_SPLIT_DEFAULT: AkteOoSplitMemory = {
  editorPct: AKTE_OO_EDITOR_DEFAULT,
  chatClosed: false,
};

export function clampAkteOoEditorPct(pct: number): number {
  if (!Number.isFinite(pct)) return AKTE_OO_EDITOR_DEFAULT;
  return Math.min(78, Math.max(42, Math.round(pct)));
}

function readOoSplitMap(): Record<string, unknown> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(PORTAL_AKTE_OO_SPLIT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Record<string, unknown>;
  } catch {
    return {};
  }
}

function ooSplitRow(row: unknown): { w: number; c: 0 | 1 } | null {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return null;
  const rec = row as Record<string, unknown>;
  if (Object.keys(rec).some((name) => name !== 'w' && name !== 'c')) return null;
  if (typeof rec.w !== 'number' || !Number.isFinite(rec.w)) return null;
  if (rec.c !== 0 && rec.c !== 1) return null;
  return { w: clampAkteOoEditorPct(rec.w), c: rec.c };
}

export function recallAkteOoSplit(akteId: string): AkteOoSplitMemory {
  const key = safeAkteStorageKey(akteId);
  if (!key) return AKTE_OO_SPLIT_DEFAULT;
  const row = ooSplitRow(readOoSplitMap()[key]);
  if (!row) return AKTE_OO_SPLIT_DEFAULT;
  return { editorPct: row.w, chatClosed: row.c === 1 };
}

export function rememberAkteOoSplit(akteId: string, memory: AkteOoSplitMemory): void {
  const key = safeAkteStorageKey(akteId);
  if (!key || typeof localStorage === 'undefined') return;
  const next: Record<string, { w: number; c: 0 | 1 }> = {};
  for (const [id, row] of Object.entries(readOoSplitMap())) {
    if (!safeAkteStorageKey(id)) continue;
    const clean = ooSplitRow(row);
    if (!clean) continue;
    next[id] = clean;
  }
  next[key] = {
    w: clampAkteOoEditorPct(memory.editorPct),
    c: memory.chatClosed ? 1 : 0,
  };
  try {
    localStorage.setItem(PORTAL_AKTE_OO_SPLIT_KEY, JSON.stringify(next));
  } catch {
    /* Speicher voll — die Fläche bleibt benutzbar. */
  }
}

/** Reservierter Map-Schlüssel: zuletzt geöffnete Akte (nur ID), kein Titel. */
const LAST_OPEN_META_KEY = '__lastOpen';

/**
 * Merkt, welche Akte gerade im Rahmen offen ist.
 * Bewusstes Schließen (Alle Akten / Hauptreiter Akten) löscht den Eintrag.
 * Beim Rückkehr von Suche/Diktat/anderen Reitern wird die ID wieder gelesen.
 */
export function rememberPortalAkteRahmenOpen(akteId: string | null): void {
  if (typeof localStorage === 'undefined') return;
  const map = readMemoryMap();
  if (!akteId) {
    delete map[LAST_OPEN_META_KEY];
  } else {
    const key = safeAkteStorageKey(akteId);
    if (!key) return;
    map[LAST_OPEN_META_KEY] = { a: key };
  }
  try {
    localStorage.setItem(PORTAL_AKTE_RAHMEN_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* Speicher voll oder gesperrt — die Akte bleibt benutzbar. */
  }
}

/** Zuletzt offen gehaltene Akte-ID, oder leer nach bewusstem Schließen. */
export function recallPortalAkteRahmenOpen(): string {
  const row = readMemoryMap()[LAST_OPEN_META_KEY];
  if (!row || typeof row !== 'object' || Array.isArray(row)) return '';
  const id = typeof (row as { a?: unknown }).a === 'string' ? (row as { a: string }).a : '';
  return safeAkteStorageKey(id);
}

/**
 * Hauptreiter Akten: Fallliste nur bei Klick, während man schon in Akten ist.
 * Rückkehr von Diktat/Suche/anderen Reitern: Position behalten (kein Home-Tick).
 */
export function aktenNavShouldShowFallliste(currentView: string, nextView: string): boolean {
  return nextView === 'akten' && currentView === 'akten';
}

export type AkteRahmenRestorePlan = 'wait' | 'restore' | 'list' | 'clear-stale';

/**
 * Nach Remount (View war weg): gemerkte offene Akte wieder öffnen.
 * Leere erste Panel-Liste darf die Merkliste nicht löschen — sonst Fallliste statt Akte.
 */
export function planAkteRahmenRestoreAfterViewReturn(input: {
  lastOpenAkteId: string;
  akten: ReadonlyArray<{ id: string }>;
  /** true erst nach geladener Liste (nicht der leere Erst-Render). */
  listReady: boolean;
}): AkteRahmenRestorePlan {
  const openId = safeAkteStorageKey(input.lastOpenAkteId);
  if (!openId) return 'list';
  if (input.akten.some((row) => row.id === openId)) return 'restore';
  if (!input.listReady || input.akten.length === 0) return 'wait';
  return 'clear-stale';
}

/**
 * Räume dieser Akte aus einem bereits geparsten Store-Katalog.
 * Ohne akte_id oder mit fremder akte_id: nicht in der Akten-Chatliste.
 */
export function elementRoomsForAkte(
  catalog: readonly ElementRoomOption[],
  akteId: string,
): ElementRoomOption[] {
  const id = akteId.trim();
  if (!id) return [];
  const out: ElementRoomOption[] = [];
  for (let i = 0; i < catalog.length; i++) {
    const room = catalog[i];
    const bound = typeof room.akte_id === 'string' ? room.akte_id.trim() : '';
    if (!bound || bound !== id) continue;
    if (!isHonestMatrixRoomId(room.id) || isStubMatrixRoomId(room.id)) continue;
    out.push(room);
  }
  return out;
}

/** GET-Körper nur bei source store. Sonst leer — kein Stub, kein zweiter Katalog. */
export function elementRoomsForAkteFromStore(data: unknown, akteId: string): ElementRoomOption[] {
  return elementRoomsForAkte(readElementRoomsResponse(data), akteId);
}

export type AkteFristRef = {
  akteId?: string | null;
  name?: string | null;
  datum?: string | null;
  /** false = vorbereitet und zählt nicht. Fehlt oder true = scharf. */
  scharf?: boolean;
};

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

export function akteLocalToday(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function formatAkteDay(iso?: string | null): string {
  const match = ISO_DAY.exec(iso?.trim() ?? '');
  if (!match) return '';
  return `${match[3]}.${match[2]}.${match[1]}`;
}

/** Nächste Frist nur dieser Akte, ab heute. Fremde Akte und Vergangenheit bleiben draußen. */
export function nextAkteFrist(
  rows: readonly AkteFristRef[],
  akteId: string,
  today: string,
): { name: string; datum: string } | null {
  const id = akteId.trim();
  const day = today.trim().slice(0, 10);
  if (!id || !ISO_DAY.test(day)) return null;
  let best: { name: string; datum: string } | null = null;
  for (const row of rows) {
    if ((row.akteId || '').trim() !== id) continue;
    if (row.scharf === false) continue;
    const name = row.name?.trim() ?? '';
    const datum = row.datum?.trim().slice(0, 10) ?? '';
    if (!name || !ISO_DAY.test(datum) || datum < day) continue;
    if (!best || datum < best.datum) best = { name, datum };
  }
  return best;
}

/** Früheste scharfe Frist über alle Akten. Vorbereitet (Haken aus) zählt nicht. */
export function nextScharfeFrist(
  rows: readonly AkteFristRef[],
  today: string,
): { akteId: string; name: string; datum: string } | null {
  const day = today.trim().slice(0, 10);
  if (!ISO_DAY.test(day)) return null;
  const akteIds: string[] = [];
  for (const row of rows) {
    const id = (row.akteId || '').trim();
    if (id && !akteIds.includes(id)) akteIds.push(id);
  }
  let best: { akteId: string; name: string; datum: string } | null = null;
  for (const akteId of akteIds) {
    const hit = nextAkteFrist(rows, akteId, day);
    if (!hit) continue;
    if (!best || hit.datum < best.datum) best = { akteId, ...hit };
  }
  return best;
}

export type AkteVerlaufAnchor =
  | { kind: 'frist'; name: string; datum: string }
  | { kind: 'dokument'; id: string; title: string }
  | { kind: 'chat'; id: string };

/** Eine Zeile offen. Gleicher Klick klappt zu, ein anderer ersetzt. */
export function toggleAkteRowLage(current: string | null, clicked: string): string | null {
  const id = clicked.trim();
  if (!id) return current;
  return current === id ? null : id;
}

export function akteLageSection(kind: AkteVerlaufAnchor['kind']): PortalAkteRahmenSectionId {
  if (kind === 'frist') return 'fristen';
  if (kind === 'dokument') return 'onlyoffice';
  return 'arko';
}

/** Nur echte Anker. Ohne Buchung kein Chat. Ohne Treffer leere Liste. */
export function buildAkteVerlaufAnchors(input: {
  akteId: string;
  today: string;
  fristen: readonly AkteFristRef[];
  lastDocument?: { id?: string | null; title?: string | null; akteId?: string | null } | null;
  chatId?: string | null;
  arkoBooked: boolean;
}): AkteVerlaufAnchor[] {
  const akteId = input.akteId.trim();
  if (!akteId) return [];
  const out: AkteVerlaufAnchor[] = [];
  const frist = nextAkteFrist(input.fristen, akteId, input.today);
  if (frist) out.push({ kind: 'frist', name: frist.name, datum: frist.datum });
  const docAkte = input.lastDocument?.akteId?.trim() ?? '';
  const docTitle = input.lastDocument?.title?.trim() ?? '';
  const docId = safeFocusId(input.lastDocument?.id);
  if (docAkte === akteId && docTitle && docId) {
    out.push({ kind: 'dokument', id: docId, title: docTitle });
  }
  const chatId = safeFocusId(input.chatId);
  if (input.arkoBooked && chatId) out.push({ kind: 'chat', id: chatId });
  return out;
}
